// src/components/OrgInstallWrapper.js
import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useOrgInstallStore } from "../stores";
import {
  fetchOrganizationsWithInstallations,
  cacheOrganizationsToBackend,
} from "../utils/graphqlClient";
import logger from "../utils/logger";
import config from "../config";
import OrganizationSelector from "./OrganizationSelector";
import InstallationSelector from "./InstallationSelector";
import AppContent from "../AppContent";
import InstallationManagementModal from "../InstallationManagementModal";
import OrganizationManagementModal from "../OrganizationManagementModal";
import { DevStreamPane } from "./DevStream";
import { useDevStream } from "../hooks/useDevStream";
import "./Callback.css"; // Import Callback styles for consistent loading screens

/**
 * Wrapper component that implements Smart Auto-Navigation (Option C):
 * - Loading screen while fetching organizations
 * - Auto-skip selectors if user has 1 org + 1 installation
 * - Show selectors if multiple options available
 * - Show error messages if 0 organizations
 *
 * Saves selections to localStorage and passes them to AppContent
 */
function OrgInstallWrapper() {
  const { getAccessToken, logout, user } = useAuth();

  // Zustand store - selective subscriptions to avoid unnecessary re-renders
  const organizations = useOrgInstallStore((state) => state.organizations);
  const selectedOrganization = useOrgInstallStore(
    (state) => state.selectedOrganization,
  );
  const selectedInstallation = useOrgInstallStore(
    (state) => state.selectedInstallation,
  );
  const loading = useOrgInstallStore((state) => state.loading);
  const loadingProgress = useOrgInstallStore((state) => state.loadingProgress);
  const view = useOrgInstallStore((state) => state.view);
  const cameFromApp = useOrgInstallStore((state) => state.cameFromApp);
  const savedOrganization = useOrgInstallStore(
    (state) => state.savedOrganization,
  );
  const savedInstallation = useOrgInstallStore(
    (state) => state.savedInstallation,
  );
  const deletedCurrentInstallation = useOrgInstallStore(
    (state) => state.deletedCurrentInstallation,
  );
  const showInstallationManagement = useOrgInstallStore(
    (state) => state.showInstallationManagement,
  );
  const showOrganizationManagement = useOrgInstallStore(
    (state) => state.showOrganizationManagement,
  );
  const orgSettings = useOrgInstallStore((state) => state.orgSettings);

  // Actions from store
  const setOrganizations = useOrgInstallStore(
    (state) => state.setOrganizations,
  );
  const setSelectedOrganization = useOrgInstallStore(
    (state) => state.setSelectedOrganization,
  );
  const setSelectedInstallation = useOrgInstallStore(
    (state) => state.setSelectedInstallation,
  );
  const setLoading = useOrgInstallStore((state) => state.setLoading);
  const setLoadingProgress = useOrgInstallStore(
    (state) => state.setLoadingProgress,
  );
  const setView = useOrgInstallStore((state) => state.setView);
  const setCameFromApp = useOrgInstallStore((state) => state.setCameFromApp);
  const setSavedOrganization = useOrgInstallStore(
    (state) => state.setSavedOrganization,
  );
  const setSavedInstallation = useOrgInstallStore(
    (state) => state.setSavedInstallation,
  );
  const setDeletedCurrentInstallation = useOrgInstallStore(
    (state) => state.setDeletedCurrentInstallation,
  );
  const setShowInstallationManagement = useOrgInstallStore(
    (state) => state.setShowInstallationManagement,
  );
  const setShowOrganizationManagement = useOrgInstallStore(
    (state) => state.setShowOrganizationManagement,
  );
  const setOrgSettings = useOrgInstallStore((state) => state.setOrgSettings);

  // Local state for dev streams (not in store since it's UI-specific to wrapper)

  // Developer Streams - persist visibility across all views
  const [devStreamsVisible, setDevStreamsVisible] = useState(() => {
    const saved = localStorage.getItem("devStreamsVisible");
    return saved === "true";
  });

  // DevStream pane width (in viewport width units)
  const [devStreamWidth, setDevStreamWidth] = useState(40); // Default 40vw

  // Persist devStreamsVisible to localStorage
  useEffect(() => {
    localStorage.setItem("devStreamsVisible", devStreamsVisible.toString());
  }, [devStreamsVisible]);

  // Listen for pop-in requests from popout window
  useEffect(() => {
    const handlePopinRequest = (e) => {
      if (e.key === "devStreamPopinRequested" && e.newValue === "true") {
        // Reopen the inline pane
        setDevStreamsVisible(true);

        // Clean up the flag
        localStorage.removeItem("devStreamPopinRequested");
        localStorage.removeItem("devStreamPopout");
      }
    };

    window.addEventListener("storage", handlePopinRequest);

    return () => {
      window.removeEventListener("storage", handlePopinRequest);
    };
  }, []);

  // Initialize DevStream hook with enhanced access control
  const isFullAdmin = user?.roles?.some(
    (role) => (typeof role === "string" ? role : role?.name) === "Full Admin",
  );
  const canAccessDevStreams =
    isFullAdmin || orgSettings?.enableDevStreamsForAllRoles;
  const devStreamData = useDevStream(
    user?.userId,
    devStreamsVisible && canAccessDevStreams,
    selectedOrganization?.orgId, // Pass current org ID as fallback for new users
  );

  // Fetch organization settings when organization changes
  useEffect(() => {
    const fetchOrgSettings = async () => {
      if (!selectedOrganization?.orgId) {
        setOrgSettings({ enableDevStreamsForAllRoles: false });
        return;
      }

      const cacheKey = `orgSettings_${selectedOrganization.orgId}`;
      const cached = localStorage.getItem(cacheKey);

      // Check cache first
      if (cached) {
        try {
          const { settings, timestamp } = JSON.parse(cached);
          const age = Date.now() - timestamp;
          const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

          if (age < CACHE_TTL) {
            setOrgSettings(settings);
            return;
          }
        } catch (e) {
          logger.warn("Failed to parse cached org settings in wrapper", e);
        }
      }

      // Fetch from API
      try {
        const token = await getAccessToken();
        const response = await fetch(
          `${config.servicesApiEndpoint}/organizations/${encodeURIComponent(
            selectedOrganization.orgId,
          )}/settings`,
          {
            method: "GET",
            headers: {
              "x-api-key": config.apiKey,
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (response.ok) {
          const data = await response.json();
          setOrgSettings(data.settings);

          // Update cache
          localStorage.setItem(
            cacheKey,
            JSON.stringify({ settings: data.settings, timestamp: Date.now() }),
          );
        } else {
          logger.warn(
            "Failed to fetch org settings in wrapper, using defaults",
          );
          setOrgSettings({ enableDevStreamsForAllRoles: false });
        }
      } catch (error) {
        logger.error("Error fetching org settings in wrapper:", error);
        setOrgSettings({ enableDevStreamsForAllRoles: false });
      }
    };

    fetchOrgSettings();
  }, [selectedOrganization, getAccessToken, setOrgSettings]);

  // Listen for cross-tab settings updates
  useEffect(() => {
    if (!selectedOrganization?.orgId) return;

    const cacheKey = `orgSettings_${selectedOrganization.orgId}`;

    const handleStorageChange = (event) => {
      if (event.key === cacheKey && event.newValue) {
        try {
          const { settings } = JSON.parse(event.newValue);

          // Check if access was revoked
          const hadAccess = canAccessDevStreams;
          const willHaveAccess =
            isFullAdmin || settings?.enableDevStreamsForAllRoles;

          setOrgSettings(settings);

          // Auto-close dev streams if access was revoked
          if (hadAccess && !willHaveAccess && devStreamsVisible) {
            setDevStreamsVisible(false);
            logger.log(
              "[DevStreams] Access revoked, closing Developer Streams",
            );
          }

          logger.log("Org settings updated from another tab in wrapper");
        } catch (e) {
          logger.warn(
            "Failed to parse settings from storage event in wrapper",
            e,
          );
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [
    selectedOrganization,
    isFullAdmin,
    canAccessDevStreams,
    devStreamsVisible,
    setOrgSettings,
  ]);

  // Listen for self-revocation from current org (cross-tab sync and same-tab)
  useEffect(() => {
    const refreshOrgsAfterRevoke = async () => {
      try {
        logger.log(
          "[OrgInstallWrapper] Fetching fresh organizations after self-revocation...",
        );
        const freshOrgs = await fetchOrganizationsWithInstallations(
          getAccessToken,
          () => {
            logger.error("[OrgInstallWrapper] 401 received, logging out");
            logout("session_expired");
          },
          user?.userId,
        );

        // Update state with fresh org list (excluding revoked org)
        setOrganizations(freshOrgs);
        logger.log(
          `[OrgInstallWrapper] Organizations refreshed, now have ${freshOrgs.length} orgs`,
        );

        // Update cache
        localStorage.setItem("orgs_cache", JSON.stringify(freshOrgs));
        localStorage.setItem("orgs_cache_timestamp", Date.now().toString());

        // Now redirect to selector with updated list
        setView("selectOrg");
        logger.log(
          "[OrgInstallWrapper] Redirected to org selector with updated list",
        );
      } catch (err) {
        logger.error(
          "[OrgInstallWrapper] Failed to refresh organizations:",
          err,
        );
        // Still redirect even if refresh failed
        setView("selectOrg");
      }
    };

    const handleSelfRevocation = (event) => {
      // Detect when selectedOrgId is removed (self-revocation from other tabs)
      if (event.key === "selectedOrgId" && event.newValue === null) {
        logger.log(
          "[OrgInstallWrapper] Detected self-revocation from current org (cross-tab), refreshing orgs before redirect",
        );

        // Clear local state
        setSelectedOrganization(null);
        setSelectedInstallation(null);

        // Refresh organizations list, then redirect
        refreshOrgsAfterRevoke();
      }
    };

    const handleSelfRevocationSameTab = () => {
      // Custom event for same-tab self-revocation (storage event doesn't fire in same tab)
      logger.log(
        "[OrgInstallWrapper] Detected self-revocation from current org (same tab), refreshing orgs before redirect",
      );

      // Clear local state
      setSelectedOrganization(null);
      setSelectedInstallation(null);

      // Refresh organizations list, then redirect
      refreshOrgsAfterRevoke();
    };

    window.addEventListener("storage", handleSelfRevocation);
    window.addEventListener(
      "selfRevokedCurrentOrg",
      handleSelfRevocationSameTab,
    );

    return () => {
      window.removeEventListener("storage", handleSelfRevocation);
      window.removeEventListener(
        "selfRevokedCurrentOrg",
        handleSelfRevocationSameTab,
      );
    };
  }, [getAccessToken, logout, user?.userId, setOrganizations, setSelectedInstallation, setSelectedOrganization, setView]);

  // Debug: Track showOrganizationManagement state changes
  useEffect(() => {
    logger.log(
      "[OrgInstallWrapper] showOrganizationManagement changed:",
      showOrganizationManagement,
    );
  }, [showOrganizationManagement]);

  // Initialize: Fetch organizations and handle smart navigation
  useEffect(() => {
    const initialize = async () => {
      try {
        setLoading(true);

        // Check for cached organizations with TTL (5 minutes)
        const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
        const cachedData = localStorage.getItem("orgs_cache");
        const cacheTimestamp = localStorage.getItem("orgs_cache_timestamp");
        const now = Date.now();

        let orgs = null;

        // Use cache if valid and not expired
        if (cachedData && cacheTimestamp) {
          const age = now - parseInt(cacheTimestamp, 10);
          if (age < CACHE_TTL_MS) {
            try {
              orgs = JSON.parse(cachedData);
              logger.log(
                `[OrgInstallWrapper] Using cached organizations (age: ${Math.round(
                  age / 1000,
                )}s)`,
              );
            } catch (parseError) {
              logger.warn(
                "[OrgInstallWrapper] Failed to parse cached orgs:",
                parseError,
              );
            }
          } else {
            logger.log(
              `[OrgInstallWrapper] Cache expired (age: ${Math.round(
                age / 1000,
              )}s), fetching fresh data`,
            );
          }
        }

        // Fetch fresh data if no valid cache
        if (!orgs) {
          logger.log(
            "[OrgInstallWrapper] No valid cache, fetching from backend...",
          );
          orgs = await fetchOrganizationsWithInstallations(
            getAccessToken,
            () => {
              logger.error("[OrgInstallWrapper] 401 received, logging out");
              logout("session_expired");
            },
            user?.userId, // Pass userId for cache validation
          );

          // Cache the fresh data
          try {
            localStorage.setItem("orgs_cache", JSON.stringify(orgs));
            localStorage.setItem("orgs_cache_timestamp", now.toString());
            logger.log("[OrgInstallWrapper] Cached fresh organization data");
          } catch (cacheError) {
            logger.warn(
              "[OrgInstallWrapper] Failed to cache orgs:",
              cacheError,
            );
          }
        } else {
          // Refresh cache in background if using cached data
          fetchOrganizationsWithInstallations(
            getAccessToken,
            () => {
              logger.error(
                "[OrgInstallWrapper] 401 received during background refresh",
              );
              logout("session_expired");
            },
            user?.userId, // Pass userId for cache validation
          )
            .then((freshOrgs) => {
              try {
                localStorage.setItem("orgs_cache", JSON.stringify(freshOrgs));
                localStorage.setItem(
                  "orgs_cache_timestamp",
                  Date.now().toString(),
                );
                logger.log(
                  "[OrgInstallWrapper] Background refresh complete, cache updated",
                );
                // Update organizations state if data changed
                if (JSON.stringify(freshOrgs) !== JSON.stringify(orgs)) {
                  setOrganizations(freshOrgs);
                  logger.log(
                    "[OrgInstallWrapper] Organizations updated from background refresh",
                  );
                }
              } catch (err) {
                logger.warn(
                  "[OrgInstallWrapper] Background refresh cache update failed:",
                  err,
                );
              }
            })
            .catch((err) => {
              logger.warn(
                "[OrgInstallWrapper] Background refresh failed (non-critical):",
                err,
              );
            });
        }

        setOrganizations(orgs);

        // Cache organizations to backend so sync Lambda can discover devices
        // This is done in the background and doesn't block the UI
        cacheOrganizationsToBackend(orgs).catch((err) => {
          logger.warn("Non-critical: Failed to cache org/install data:", err);
        });

        // Check localStorage for previously selected org/installation
        const savedOrgId = localStorage.getItem("selectedOrgId");
        const savedInstallId = localStorage.getItem("selectedInstallationId");

        // Smart Auto-Navigation Logic (Option C)
        if (orgs.length === 0) {
          // 0 organizations: Show error
          setView("selectOrg");
        } else if (orgs.length === 1 && orgs[0].installations.length === 1) {
          // 1 org + 1 installation: Auto-navigate to app
          const org = orgs[0];
          const installation = org.installations[0];
          setSelectedOrganization(org);
          setSelectedInstallation(installation);
          localStorage.setItem("selectedOrgId", org.orgId);
          localStorage.setItem(
            "selectedInstallationId",
            installation.installationId,
          );
          setView("app");
        } else if (savedOrgId && savedInstallId) {
          // Try to restore previous selection
          const savedOrg = orgs.find((o) => o.orgId === savedOrgId);
          if (savedOrg) {
            const savedInstall = savedOrg.installations.find(
              (i) => i.installationId === savedInstallId,
            );
            if (savedInstall) {
              // Valid saved selection exists
              setSelectedOrganization(savedOrg);
              setSelectedInstallation(savedInstall);
              setView("app");
            } else {
              // Saved installation not found, show installation selector
              setSelectedOrganization(savedOrg);
              setView("selectInstallation");
            }
          } else {
            // Saved org not found, show org selector
            setView("selectOrg");
          }
        } else if (savedOrgId) {
          // Org saved but no installation - show installation selector for that org
          const savedOrg = orgs.find((o) => o.orgId === savedOrgId);
          if (savedOrg) {
            setSelectedOrganization(savedOrg);
            setView("selectInstallation");
          } else {
            // Saved org not found, show org selector
            setView("selectOrg");
          }
        } else {
          // No saved selection, show org selector
          setView("selectOrg");
        }
      } catch (err) {
        logger.error("Error initializing org/installation:", err);
        setView("selectOrg");
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, [getAccessToken, logout, user?.userId, setLoading, setOrganizations, setSelectedInstallation, setSelectedOrganization, setView]);

  // Progress indicator effect - simulates incremental progress during loading
  useEffect(() => {
    if (!loading) {
      setLoadingProgress(0); // Reset when not loading
      return;
    }

    setLoadingProgress(0);
    const startTime = Date.now();

    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      // Use logarithmic curve: fast initial progress, slows near 100%
      // Progress approaches but never quite reaches 100% until actual load completes
      // Adjusted to take ~8-10 seconds to reach 95% (matching actual load time)
      const progress = Math.min(95, (1 - Math.exp(-elapsed / 5000)) * 100);
      setLoadingProgress(Math.round(progress));
    }, 100);

    return () => clearInterval(interval);
  }, [loading, setLoadingProgress]);

  // Watch for installation being deleted - if installation becomes null while org exists, show selector
  useEffect(() => {
    if (
      selectedOrganization &&
      !selectedInstallation &&
      !loading &&
      view === "app"
    ) {
      logger.log(
        "[OrgInstallWrapper] Installation deleted, switching to installation selector",
      );
      // Installation was deleted, need to select a new one
      setView("selectInstallation");
      setDeletedCurrentInstallation(true); // Mark that we're forcing selection
    }
  }, [selectedOrganization, selectedInstallation, loading, view, setDeletedCurrentInstallation, setView]);

  // Handler: User selects an organization
  const handleSelectOrganization = (org) => {
    setSelectedOrganization(org);
    localStorage.setItem("selectedOrgId", org.orgId);

    if (org.installations.length === 0) {
      // 0 installations: Clear previous installation and show error in installation selector
      setSelectedInstallation(null);
      localStorage.removeItem("selectedInstallationId");
      setView("selectInstallation");
    } else if (org.installations.length === 1) {
      // 1 installation: Auto-select and navigate to app
      const installation = org.installations[0];
      setSelectedInstallation(installation);
      localStorage.setItem(
        "selectedInstallationId",
        installation.installationId,
      );
      // Clear saved state since change was completed
      setCameFromApp(false);
      setSavedOrganization(null);
      setSavedInstallation(null);
      setView("app");
    } else {
      // Multiple installations: Clear previous installation and show selector
      setSelectedInstallation(null);
      localStorage.removeItem("selectedInstallationId");
      setView("selectInstallation");
    }
  };

  // Handler: User selects an installation
  const handleSelectInstallation = useCallback((installation) => {
    setSelectedInstallation(installation);
    localStorage.setItem("selectedInstallationId", installation.installationId);
    // Clear saved state since change was completed
    setCameFromApp(false);
    setSavedOrganization(null);
    setSavedInstallation(null);
    setDeletedCurrentInstallation(false); // Reset deleted flag
    setView("app");
  }, [setSelectedInstallation, setCameFromApp, setSavedOrganization, setSavedInstallation, setDeletedCurrentInstallation, setView]);

  // Check for pending installation switch after org change
  // This runs when view changes to selectInstallation and we have installations
  useEffect(() => {
    if (
      view === "selectInstallation" &&
      selectedOrganization &&
      selectedOrganization.installations.length > 0
    ) {
      const pendingSwitch = sessionStorage.getItem("pendingInstallationSwitch");
      if (pendingSwitch) {
        try {
          const { installationId, installationName } =
            JSON.parse(pendingSwitch);
          // Find the installation in the current organization
          const targetInstallation = selectedOrganization.installations.find(
            (inst) => inst.installationId === installationId,
          );
          if (targetInstallation) {
            logger.log(
              `[OrgInstallWrapper] Auto-switching to pending installation: ${installationName}`,
            );
            // Clear the pending switch
            sessionStorage.removeItem("pendingInstallationSwitch");
            // Automatically select the installation
            handleSelectInstallation(targetInstallation);
          } else {
            logger.warn(
              `[OrgInstallWrapper] Pending installation ${installationName} not found in org ${selectedOrganization.name}`,
            );
            // Clear the pending switch anyway to avoid getting stuck
            sessionStorage.removeItem("pendingInstallationSwitch");
          }
        } catch (err) {
          logger.error(
            "[OrgInstallWrapper] Error parsing pending installation switch:",
            err,
          );
          sessionStorage.removeItem("pendingInstallationSwitch");
        }
      }
    }
  }, [view, selectedOrganization, handleSelectInstallation]);

  // Handler: User clicks "Back to Organizations"
  const handleBackToOrganizations = () => {
    // Only clear installation if we're in initial selection flow (not from app)
    if (!cameFromApp) {
      setSelectedInstallation(null);
      localStorage.removeItem("selectedInstallationId");
    }
    setView("selectOrg");
    // Don't reset cameFromApp - keep it so Back to App button shows
  };

  // Handler: User wants to change organization (from menu)
  const handleChangeOrganization = () => {
    setSavedOrganization(selectedOrganization); // Save current organization
    setSavedInstallation(selectedInstallation); // Save current installation
    setCameFromApp(true); // Mark that we came from app
    setView("selectOrg");
  };

  // Handler: User wants to change installation (from menu)
  // forceChange: If true, user deleted current installation and must select new one (no Back button)
  const handleChangeInstallation = (forceChange = false) => {
    logger.log("[OrgInstallWrapper] handleChangeInstallation called", {
      forceChange,
      selectedOrganization: selectedOrganization?.name,
      selectedInstallation: selectedInstallation?.name,
    });

    if (forceChange) {
      // Current installation was deleted - clear saved state and force selection
      setSavedOrganization(null);
      setSavedInstallation(null);
      setCameFromApp(false);
      setDeletedCurrentInstallation(true);
      logger.log("[OrgInstallWrapper] Force change - setCameFromApp(false)");
    } else {
      // Normal installation change - allow Back to App
      setSavedOrganization(selectedOrganization); // Save current organization
      setSavedInstallation(selectedInstallation); // Save current installation
      setCameFromApp(true); // Mark that we came from app
      setDeletedCurrentInstallation(false);
      logger.log("[OrgInstallWrapper] Normal change - setCameFromApp(true)", {
        savedOrg: selectedOrganization?.name,
        savedInstall: selectedInstallation?.name,
      });
    }
    setView("selectInstallation");
  };

  // Handler: User cancels org change and goes back to app
  const handleCancelOrgChange = () => {
    // Restore saved organization and installation
    if (savedOrganization) {
      setSelectedOrganization(savedOrganization);
    }
    if (savedInstallation) {
      setSelectedInstallation(savedInstallation);
    }
    setCameFromApp(false); // Reset flag
    setSavedOrganization(null); // Clear saved organization
    setSavedInstallation(null); // Clear saved installation
    setView("app");
  };

  // Handler: User cancels installation change and goes back to app
  const handleCancelInstallationChange = () => {
    // Don't allow cancel if current installation was deleted
    if (deletedCurrentInstallation) {
      logger.log(
        "[OrgInstallWrapper] Cannot cancel - current installation was deleted",
      );
      return;
    }

    // Restore saved organization and installation
    if (savedOrganization) {
      setSelectedOrganization(savedOrganization);
    }
    if (savedInstallation) {
      setSelectedInstallation(savedInstallation);
    }
    setCameFromApp(false); // Reset flag
    setSavedOrganization(null); // Clear saved organization
    setSavedInstallation(null); // Clear saved installation
    setDeletedCurrentInstallation(false); // Reset deleted flag
    setView("app");
  };

  // Handler: Refresh installations for current organization
  const handleRefreshInstallations = async () => {
    try {
      logger.log("[OrgInstallWrapper] Refreshing installations...");
      const orgs = await fetchOrganizationsWithInstallations(
        getAccessToken,
        () => {
          logger.error(
            "[OrgInstallWrapper] 401 received during refresh, logging out",
          );
          logout("session_expired");
        },
        user?.userId, // Pass userId for cache validation
      );

      // Invalidate cache and update with fresh data
      try {
        localStorage.setItem("orgs_cache", JSON.stringify(orgs));
        localStorage.setItem("orgs_cache_timestamp", Date.now().toString());
        logger.log(
          "[OrgInstallWrapper] Cache invalidated and updated with fresh data",
        );
      } catch (cacheError) {
        logger.warn("[OrgInstallWrapper] Failed to update cache:", cacheError);
      }

      setOrganizations(orgs);

      // Update selected organization if it still exists (same pattern as handleRefreshOrganizations)
      if (selectedOrganization) {
        const updatedOrg = orgs.find(
          (o) => o.orgId === selectedOrganization.orgId,
        );
        if (updatedOrg) {
          setSelectedOrganization(updatedOrg);
          // Update selected installation if it still exists
          if (selectedInstallation) {
            const updatedInstall = updatedOrg.installations.find(
              (i) => i.installationId === selectedInstallation.installationId,
            );
            if (updatedInstall) {
              setSelectedInstallation(updatedInstall);
            } else {
              // Current installation was deleted
              logger.log(
                "[OrgInstallWrapper] Current installation no longer exists after refresh",
              );
              setSelectedInstallation(null);
              localStorage.removeItem("selectedInstallationId");
            }
          }
        } else {
          // Current organization was deleted!
          logger.log(
            "[OrgInstallWrapper] Current organization no longer exists after refresh",
          );
          setSelectedOrganization(null);
          setSelectedInstallation(null);
          localStorage.removeItem("selectedOrgId");
          localStorage.removeItem("selectedInstallationId");
        }
      }

      logger.log("[OrgInstallWrapper] Installations refreshed successfully");
    } catch (err) {
      logger.error("Error refreshing installations:", err);
    }
  };

  // Handler: Open installation management from selector (when no installations exist)
  const handleOpenInstallationManagement = () => {
    logger.log(
      "[OrgInstallWrapper] Opening installation management from selector",
    );
    setShowInstallationManagement(true);
  };

  // Handler: Open organization management from selector (when no organizations exist)
  const handleOpenOrganizationManagement = () => {
    logger.log(
      "[OrgInstallWrapper] Opening organization management from selector",
    );
    setShowOrganizationManagement(true);
  };

  // Handler: Refresh organizations list
  const handleRefreshOrganizations = async () => {
    try {
      logger.log("[OrgInstallWrapper] Refreshing organizations...");
      logger.log(
        "[OrgInstallWrapper] Current organizations BEFORE refresh:",
        organizations.map((o) => ({
          orgId: o.orgId,
          name: o.name,
          romanworksAuthzStatus: o.romanworksAuthzStatus,
        })),
      );

      const orgs = await fetchOrganizationsWithInstallations(
        getAccessToken,
        () => {
          logger.error(
            "[OrgInstallWrapper] 401 received during refresh, logging out",
          );
          logout("session_expired");
        },
        user?.userId, // Pass userId for cache validation
      );

      logger.log(
        "[OrgInstallWrapper] Fresh orgs from backend:",
        orgs.map((o) => ({
          orgId: o.orgId,
          name: o.name,
          romanworksAuthzStatus: o.romanworksAuthzStatus,
        })),
      );

      // Check if current org was deleted BEFORE updating any state
      const currentOrgStillExists =
        selectedOrganization &&
        orgs.find((o) => o.orgId === selectedOrganization.orgId);

      // Preserve romanworksAuthzStatus for all organizations by merging with existing data
      const mergedOrgs = orgs.map((freshOrg) => {
        const existingOrg = organizations.find(
          (o) => o.orgId === freshOrg.orgId,
        );
        const merged = {
          ...freshOrg,
          romanworksAuthzStatus:
            freshOrg.romanworksAuthzStatus ||
            existingOrg?.romanworksAuthzStatus,
        };
        logger.log(`[OrgInstallWrapper] Merging org ${freshOrg.orgId}:`, {
          fresh: freshOrg.romanworksAuthzStatus,
          existing: existingOrg?.romanworksAuthzStatus,
          final: merged.romanworksAuthzStatus,
        });
        return merged;
      });

      logger.log(
        "[OrgInstallWrapper] Merged organizations:",
        mergedOrgs.map((o) => ({
          orgId: o.orgId,
          name: o.name,
          romanworksAuthzStatus: o.romanworksAuthzStatus,
        })),
      );

      // Invalidate cache and update with merged data (preserving authorization status)
      try {
        localStorage.setItem("orgs_cache", JSON.stringify(mergedOrgs));
        localStorage.setItem("orgs_cache_timestamp", Date.now().toString());
        logger.log("[OrgInstallWrapper] Cache updated with merged data");
      } catch (cacheError) {
        logger.warn("[OrgInstallWrapper] Failed to update cache:", cacheError);
      }

      setOrganizations(mergedOrgs);

      logger.log("[OrgInstallWrapper] State updated with merged organizations");

      // Update selected organization if it still exists
      if (selectedOrganization) {
        const updatedOrg = mergedOrgs.find(
          (o) => o.orgId === selectedOrganization.orgId,
        );
        if (updatedOrg) {
          logger.log("[OrgInstallWrapper] Updating selected organization:", {
            orgId: updatedOrg.orgId,
            name: updatedOrg.name,
            romanworksAuthzStatus: updatedOrg.romanworksAuthzStatus,
          });
          setSelectedOrganization(updatedOrg);
          // Update selected installation if it still exists
          if (selectedInstallation) {
            const updatedInstall = updatedOrg.installations.find(
              (i) => i.installationId === selectedInstallation.installationId,
            );
            if (updatedInstall) {
              setSelectedInstallation(updatedInstall);
            } else {
              // Current installation was deleted
              logger.log(
                "[OrgInstallWrapper] Current installation no longer exists after refresh",
              );
              setSelectedInstallation(null);
              localStorage.removeItem("selectedInstallationId");
            }
          }
        } else {
          // Current organization was deleted!
          logger.log(
            "[OrgInstallWrapper] Current organization no longer exists after refresh",
          );
          setSelectedOrganization(null);
          setSelectedInstallation(null);
          localStorage.removeItem("selectedOrgId");
          localStorage.removeItem("selectedInstallationId");
        }
      }

      // If current org was deleted, clear navigation state (do this after all other state updates)
      if (!currentOrgStillExists) {
        logger.log(
          "[OrgInstallWrapper] Clearing navigation state for deleted org",
        );
        setCameFromApp(false);
        setSavedOrganization(null);
        setSavedInstallation(null);
      }

      logger.log("[OrgInstallWrapper] Organizations refreshed successfully");
    } catch (err) {
      logger.error("Error refreshing organizations:", err);
      throw err;
    }
  };

  // Render based on current view
  // Show loading modal immediately if still initializing
  if (loading) {
    return (
      <div className="callback-container">
        <div className="callback-content">
          <div className="callback-spinner"></div>
          <h2>Loading Application...</h2>
          <p>Loading organizations... {loadingProgress}%</p>
        </div>
      </div>
    );
  }

  // Check if we have valid selection
  const hasValidSelection = selectedOrganization && selectedInstallation;

  // Check if we can actually go back to app (only if saved org/install still exist)
  const canGoBackToApp =
    cameFromApp &&
    savedOrganization &&
    organizations.find((org) => org.orgId === savedOrganization.orgId);

  // Log the canGoBackToApp calculation for debugging
  logger.log("[OrgInstallWrapper] canGoBackToApp calculation", {
    cameFromApp,
    savedOrganization: savedOrganization?.name,
    savedOrganizationId: savedOrganization?.orgId,
    foundInOrgs: organizations.find(
      (org) => org.orgId === savedOrganization?.orgId,
    )?.name,
    canGoBackToApp: !!canGoBackToApp,
    hasValidSelection,
    view,
    deletedCurrentInstallation,
  });

  if (!hasValidSelection) {
    // Initial flow: No valid selection yet - show appropriate selector without overlay
    if (view === "selectOrg" || view === "selectInstallation") {
      if (view === "selectOrg") {
        logger.log("[OrgInstallWrapper] Rendering OrganizationSelector", {
          user,
          roles: user?.roles,
          canGoBackToApp,
        });
        return (
          <>
            <OrganizationSelector
              organizations={organizations}
              onSelectOrganization={handleSelectOrganization}
              onCancel={canGoBackToApp ? handleCancelOrgChange : null}
              onCreateOrganization={handleOpenOrganizationManagement}
              onManageOrganizations={handleOpenOrganizationManagement}
              userRoles={user?.roles}
            />
            {showOrganizationManagement && (
              <OrganizationManagementModal
                isOpen={showOrganizationManagement}
                onClose={() => setShowOrganizationManagement(false)}
                organizations={organizations}
                currentOrganizationId={selectedOrganization?.orgId}
                userProfile={user}
                getAccessToken={getAccessToken}
                onOrganizationsChanged={handleRefreshOrganizations}
                onSwitchOrganization={(organization) => {
                  handleSelectOrganization(organization);
                  // Always close the management modal - handleSelectOrganization will set the correct view
                  setShowOrganizationManagement(false);
                }}
                onChangeOrganization={handleChangeOrganization}
                onOpenInstallationManagement={(organizationId) => {
                  logger.log(
                    "[OrgInstallWrapper] onOpenInstallationManagement called from selector context",
                    { organizationId, currentView: view },
                  );

                  setShowOrganizationManagement(false);

                  // If a specific organization was clicked, select it first
                  if (organizationId) {
                    const targetOrg = organizations.find(
                      (org) => org.orgId === organizationId,
                    );
                    if (targetOrg) {
                      logger.log(
                        "[OrgInstallWrapper] Setting selected organization to:",
                        targetOrg.name,
                      );
                      setSelectedOrganization(targetOrg);
                      localStorage.setItem("selectedOrgId", targetOrg.orgId);

                      // Use setTimeout to ensure state updates complete before changing view
                      setTimeout(() => {
                        setView("selectInstallation");
                        setTimeout(() => {
                          setShowInstallationManagement(true);
                        }, 0);
                      }, 0);
                    }
                  } else {
                    // No specific org, just open modal for current org
                    setShowInstallationManagement(true);
                  }
                }}
              />
            )}
          </>
        );
      }

      if (view === "selectInstallation") {
        logger.log(
          "[OrgInstallWrapper] Rendering InstallationSelector (initial flow)",
          {
            canGoBackToApp: !!canGoBackToApp,
            deletedCurrentInstallation,
            willShowBackToApp: !!(
              canGoBackToApp && !deletedCurrentInstallation
            ),
            hasValidSelection,
          },
        );
        return (
          <>
            <InstallationSelector
              organization={selectedOrganization}
              installations={selectedOrganization?.installations || []}
              onSelectInstallation={handleSelectInstallation}
              onBack={handleBackToOrganizations}
              onCancel={
                canGoBackToApp && !deletedCurrentInstallation
                  ? handleCancelInstallationChange
                  : null
              }
              onCreateInstallation={handleOpenInstallationManagement}
              onManageInstallations={handleOpenInstallationManagement}
              userRoles={user?.roles}
            />
            {showInstallationManagement && (
              <InstallationManagementModal
                isOpen={showInstallationManagement}
                onClose={() => setShowInstallationManagement(false)}
                installations={selectedOrganization?.installations || []}
                currentInstallationId={selectedInstallation?.installationId}
                organizationId={selectedOrganization?.orgId}
                userProfile={user}
                getAccessToken={getAccessToken}
                onInstallationsChanged={handleRefreshInstallations}
                onSwitchInstallation={(installation) => {
                  handleSelectInstallation(installation);
                  setShowInstallationManagement(false);
                }}
                onChangeInstallation={handleChangeInstallation}
              />
            )}
          </>
        );
      }
    }

    // Fallback
    return (
      <div className="callback-container">
        <div className="callback-content">
          <div className="callback-spinner"></div>
          <h2>Loading Application...</h2>
          <p>Initializing...</p>
        </div>
      </div>
    );
  }

  // We have valid selection - always render AppContent at the same position
  // Overlay selectors on top when needed (cameFromApp=true)
  return (
    <>
      {canGoBackToApp &&
        (view === "selectOrg" || view === "selectInstallation") && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9999,
              backgroundColor: "var(--bg-primary)",
            }}
          >
            {view === "selectOrg" &&
              (() => {
                logger.log(
                  "[OrgInstallWrapper OVERLAY] Rendering OrganizationSelector",
                  {
                    user,
                    roles: user?.roles,
                    canGoBackToApp,
                    showOrganizationManagement,
                  },
                );
                return (
                  <>
                    <OrganizationSelector
                      organizations={organizations}
                      onSelectOrganization={handleSelectOrganization}
                      onCancel={handleCancelOrgChange}
                      onCreateOrganization={handleOpenOrganizationManagement}
                      onManageOrganizations={handleOpenOrganizationManagement}
                      userRoles={user?.roles}
                    />
                    {showOrganizationManagement && (
                      <OrganizationManagementModal
                        isOpen={showOrganizationManagement}
                        onClose={() => {
                          setShowOrganizationManagement(false);
                        }}
                        organizations={organizations}
                        currentOrganizationId={selectedOrganization?.orgId}
                        userProfile={user}
                        getAccessToken={getAccessToken}
                        onOrganizationsChanged={handleRefreshOrganizations}
                        onSwitchOrganization={(organization) => {
                          handleSelectOrganization(organization);
                          setShowOrganizationManagement(false);
                        }}
                        onChangeOrganization={handleChangeOrganization}
                        onOpenInstallationManagement={(organizationId) => {
                          logger.log(
                            "[OrgInstallWrapper] onOpenInstallationManagement called from selector context",
                            { organizationId, currentView: view },
                          );

                          setShowOrganizationManagement(false);

                          if (organizationId) {
                            const targetOrg = organizations.find(
                              (org) => org.orgId === organizationId,
                            );
                            if (targetOrg) {
                              logger.log(
                                "[OrgInstallWrapper] Setting selected organization to:",
                                targetOrg.name,
                              );
                              setSelectedOrganization(targetOrg);
                              localStorage.setItem(
                                "selectedOrgId",
                                targetOrg.orgId,
                              );

                              setTimeout(() => {
                                setView("selectInstallation");
                                setTimeout(() => {
                                  setShowInstallationManagement(true);
                                }, 0);
                              }, 0);
                            }
                          } else {
                            setShowInstallationManagement(true);
                          }
                        }}
                      />
                    )}
                  </>
                );
              })()}
            {view === "selectInstallation" && (
              <>
                <InstallationSelector
                  organization={selectedOrganization}
                  installations={selectedOrganization?.installations || []}
                  onSelectInstallation={handleSelectInstallation}
                  onBack={handleBackToOrganizations}
                  onCancel={
                    canGoBackToApp && !deletedCurrentInstallation
                      ? handleCancelInstallationChange
                      : null
                  }
                  onCreateInstallation={handleOpenInstallationManagement}
                  onManageInstallations={handleOpenInstallationManagement}
                  userRoles={user?.roles}
                />
                {showInstallationManagement && (
                  <InstallationManagementModal
                    isOpen={showInstallationManagement}
                    onClose={() => {
                      setShowInstallationManagement(false);
                      // Close the selector overlay when closing the modal
                      if (!deletedCurrentInstallation) {
                        handleCancelInstallationChange();
                      }
                    }}
                    installations={selectedOrganization?.installations || []}
                    currentInstallationId={selectedInstallation?.installationId}
                    organizationId={selectedOrganization.orgId}
                    userProfile={user}
                    getAccessToken={getAccessToken}
                    onInstallationsChanged={handleRefreshOrganizations}
                    onSwitchInstallation={(installation) => {
                      handleSelectInstallation(installation);
                      setShowInstallationManagement(false);
                      // Close the selector overlay after switching
                      handleCancelInstallationChange();
                    }}
                    onChangeInstallation={handleChangeInstallation}
                    devices={[]}
                  />
                )}
              </>
            )}
          </div>
        )}
      <div className="app-layout-container">
        <div
          className="main-content-wrapper"
          style={{
            flex: devStreamsVisible ? `1 1 auto` : "1",
            width: devStreamsVisible ? `${100 - devStreamWidth}vw` : "100%",
            transition: "width 0.2s ease",
          }}
        >
          <AppContent
            orgSettings={orgSettings}
            onToggleDevStreams={() => setDevStreamsVisible(!devStreamsVisible)}
            devStreamsVisible={devStreamsVisible}
            devStreamEvents={devStreamData.events}
            onSwitchOrganization={(org) => {
              handleSelectOrganization(org);
            }}
            onSwitchInstallation={(installation) => {
              setSelectedInstallation(installation);
              localStorage.setItem(
                "selectedInstallationId",
                installation.installationId,
              );
            }}
            onRefreshOrganizations={handleRefreshOrganizations}
            onRefreshInstallations={handleRefreshOrganizations}
            onChangeOrganization={handleChangeOrganization}
            onChangeInstallation={handleChangeInstallation}
            onUpdateInstallationSubscription={(installationId, newStatus) => {
              // Update installation subscription status
              const updatedOrgs = organizations.map((org) => ({
                ...org,
                installations: org.installations.map((inst) =>
                  inst.installationId === installationId
                    ? { ...inst, subscriptionStatus: newStatus }
                    : inst,
                ),
              }));
              setOrganizations(updatedOrgs);

              // Update selected installation if it matches
              if (selectedInstallation?.installationId === installationId) {
                setSelectedInstallation({
                  ...selectedInstallation,
                  subscriptionStatus: newStatus,
                });
              }
            }}
            onSetShowInstallationManagement={setShowInstallationManagement}
            onSetShowOrganizationManagement={setShowOrganizationManagement}
          />
        </div>

        {/* Developer Streams - rendered at wrapper level to persist across all views */}
        {devStreamsVisible && canAccessDevStreams && (
          <DevStreamPane
            events={devStreamData.events}
            filters={devStreamData.filters}
            updateFilter={devStreamData.updateFilter}
            ypEnabled={devStreamData.ypEnabled}
            mcEnabled={devStreamData.mcEnabled}
            toggleYP={devStreamData.toggleYP}
            toggleMC={devStreamData.toggleMC}
            clearEvents={devStreamData.clearEvents}
            sendTestEvent={devStreamData.sendTestEvent}
            connected={devStreamData.connected}
            authenticated={devStreamData.authenticated}
            connectionError={devStreamData.connectionError}
            switchOrganization={devStreamData.switchOrganization}
            selectedOrgId={devStreamData.selectedOrgId}
            maxEvents={devStreamData.maxEvents}
            updateMaxEvents={devStreamData.updateMaxEvents}
            onClose={() => setDevStreamsVisible(false)}
            isFullAdmin={isFullAdmin}
            onWidthChange={setDevStreamWidth}
            organizations={organizations} // Pass organizations to avoid localStorage race condition
          />
        )}
      </div>
    </>
  );
}

export default OrgInstallWrapper;
